import './style.css';
import { h, replace } from './dom';
import { loadBundle, type ProjectBundle } from './data';
import { getLang, onLangChange, setLang, t } from './i18n';
import { parseHash, toHash, type Route } from './router';
import { canManage, getSession, initSession, onSessionChange, roleFor, signOut } from './session';
import { backendConfigured } from './supabase';
import { adminView } from './views/admin';
import { authView } from './views/auth';
import { exportView } from './views/export';
import { projectView, type View } from './views/project';
import { projectsView } from './views/projects';
import { errorBox, loading } from './views/shared';
import { treeView } from './views/tree';

const viewEl = document.getElementById('view') as HTMLElement;
const bannerEl = document.getElementById('banner') as HTMLElement;
const navEl = document.getElementById('topnav') as HTMLElement;
const userBox = document.getElementById('userBox') as HTMLElement;
const langBtn = document.getElementById('langBtn') as HTMLButtonElement;
const versionBox = document.getElementById('versionBox') as HTMLElement;

versionBox.textContent = t('common.version', { v: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev' });
document.documentElement.lang = getLang();

langBtn.addEventListener('click', () => setLang(getLang() === 'es' ? 'en' : 'es'));

let current: View | null = null;
let bundle: ProjectBundle | null = null;
let bundleFor: string | null = null;

function banner() {
  if (getSession().demo) {
    bannerEl.hidden = false;
    bannerEl.textContent = t('auth.demo_banner');
  } else if (!backendConfigured) {
    bannerEl.hidden = false;
    bannerEl.textContent = getLang() === 'es' ? 'Backend no configurado: falta VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY. El mapa funciona; los datos no cargan todavía.' : 'Backend not configured: VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are missing. The map works; data cannot load yet.';
  } else bannerEl.hidden = true;
}

function renderChrome(route: Route) {
  langBtn.textContent = getLang() === 'es' ? 'English' : 'Español';
  const s = getSession();
  userBox.replaceChildren(
    s.user || s.demo
      ? h('span', null, h('span', { class: 'who' }, s.displayName), ' ', h('button', { type: 'button', class: 'btn btn-ghost btn-sm', onclick: () => void signOut().then(() => (location.hash = '#/projects')) }, t('auth.signout')))
      : '',
  );
  const pid = 'id' in route ? route.id : null;
  replace(
    navEl,
    h('a', { href: '#/projects', class: route.name === 'projects' ? 'on' : '' }, t('nav.projects')),
    pid ? h('a', { href: toHash({ name: 'project', id: pid }), class: route.name === 'project' || route.name === 'tree' ? 'on' : '' }, t('nav.dashboard')) : null,
    pid && roleFor(pid) ? h('a', { href: toHash({ name: 'export', id: pid }), class: route.name === 'export' ? 'on' : '' }, t('nav.export')) : null,
    pid && canManage(pid) ? h('a', { href: toHash({ name: 'admin', id: pid }), class: route.name === 'admin' ? 'on' : '' }, t('nav.admin')) : null,
  );
}

async function getBundle(id: string, force = false): Promise<ProjectBundle> {
  if (!force && bundle && bundleFor === id) return bundle;
  bundle = await loadBundle(id);
  bundleFor = id;
  return bundle;
}

async function render() {
  const route = parseHash(location.hash);
  banner();
  renderChrome(route);
  current?.destroy?.();
  current = null;
  const s = getSession();

  if (route.name === 'privacy') {
    location.href = '/privacy';
    return;
  }
  if (!s.user && !s.demo) {
    const v = authView();
    current = v;
    viewEl.replaceChildren(v.el);
    return;
  }
  if (route.name === 'projects') {
    viewEl.replaceChildren(projectsView());
    return;
  }
  const pid = route.id;
  viewEl.replaceChildren(loading());
  try {
    const b = await getBundle(pid);
    if (parseHash(location.hash).name !== route.name) return; // navigated away while loading
    const reload = async () => {
      await getBundle(pid, true);
      await render();
    };
    switch (route.name) {
      case 'project': {
        const v = projectView(b);
        current = v;
        viewEl.replaceChildren(v.el);
        break;
      }
      case 'tree':
        viewEl.replaceChildren(treeView(b, route.treeId));
        break;
      case 'export':
        viewEl.replaceChildren(exportView(b));
        break;
      case 'admin':
        viewEl.replaceChildren(canManage(pid) ? adminView(b, reload) : errorBox(new Error(t('dashboard.read_only'))));
        break;
    }
  } catch (err) {
    viewEl.replaceChildren(errorBox(err));
  }
}

window.addEventListener('hashchange', () => void render());
onLangChange(() => void render());
onSessionChange(() => {
  bundle = null;
  void render();
});

await initSession();
if (!location.hash) location.hash = '#/projects';
void render();
